import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
  Plus, ShieldCheck, ClipboardCheck, ChevronRight, Calendar as CalIcon,
  X, Search, CircleDashed, CircleCheck, CircleDot, Ban, Library,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import TemplateLibraryDialog from "@/components/TemplateLibraryDialog";
import { formatDate } from "@/lib/format";

const SCHEDULES = [
  { k: "once", l: "Once" },
  { k: "monthly", l: "Monthly" },
  { k: "quarterly", l: "Quarterly" },
  { k: "semi_annual", l: "Semi-Annual" },
  { k: "annual", l: "Annual" },
];

const STATUS_ICON = {
  open: CircleDashed,
  in_progress: CircleDot,
  blocked: Ban,
  completed: CircleCheck,
};
const STATUS_COLOR = {
  open: "#1a1a1a",
  in_progress: "#d6a900",
  blocked: "#ff4500",
  completed: "#1a8754",
};

export default function VerificationValidation() {
  const { user } = useAuth();
  const isAdmin = ["admin", "controller"].includes(user?.org_role);
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [search, setSearch] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [libOpen, setLibOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [i, u, h] = await Promise.all([
        api.get("/vv-items"),
        api.get("/users/visible-to-me").catch(() => ({ data: [] })),
        api.get("/teams").catch(() => ({ data: [] })),
      ]);
      setItems(i.data || []);
      setUsers(u.data || []);
      setHubs(h.data || []);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(); }, []);

  const uMap = useMemo(() => Object.fromEntries(users.map((x) => [x.id, x])), [users]);
  const hMap = useMemo(() => Object.fromEntries(hubs.map((x) => [x.id, x])), [hubs]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((x) =>
      !q ||
      (x.title || "").toLowerCase().includes(q) ||
      (x.display_id || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  return (
    <div className="p-4 lg:p-8 space-y-5">
      {/* Hero */}
      <section className="relative hero-navy rounded-3xl p-6 lg:p-8 text-white overflow-hidden" data-testid="vv-hero">
        <div className="absolute inset-0 dotted opacity-25" />
        <div className="absolute -top-16 -right-8 w-72 h-72 rounded-full bg-[#ff4500]/25 blur-3xl" />
        <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-3 py-1 backdrop-blur-sm">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span className="font-mono text-[10px] uppercase tracking-[0.18em]">Verification &amp; Validation · ISO / SQF</span>
            </div>
            <h1 className="mt-3 font-display font-black text-3xl lg:text-4xl leading-tight tracking-tight">Verification &amp; Validation.</h1>
            <p className="mt-2 text-sm text-white/75 max-w-xl">
              Schedule and document ISO / SQF program reviews (pest control, food defense, allergen, etc.). Each item is split into <strong>Verification</strong> — proving it&apos;s being done as written — and <strong>Validation</strong> — proving that what&apos;s written actually works.
            </p>
          </div>
          {isAdmin && (
            <div className="flex gap-2">
              <button data-testid="vv-lib-button" onClick={() => setLibOpen(true)}
                className="btn-pill bg-white text-[#1a1a1a] border border-white/30 hover:bg-white/90">
                <Library className="w-3.5 h-3.5" /> Template library
              </button>
              <button data-testid="vv-new-button" onClick={() => setNewOpen(true)}
                className="btn-pill btn-pill-primary">
                <Plus className="w-3.5 h-3.5" /> New V&amp;V item
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-2 bg-white border border-[#1a1a1a]/10 rounded-full px-3 py-1.5 shadow-soft">
          <Search className="w-3.5 h-3.5 text-[#1a1a1a]/50" />
          <input data-testid="vv-search" placeholder="Search V&amp;V items…" value={search} onChange={(e) => setSearch(e.target.value)}
            className="bg-transparent outline-none text-sm w-64 placeholder:text-[#1a1a1a]/40" />
        </div>
        <div className="text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">
          {visible.length} / {items.length} item{items.length === 1 ? "" : "s"}
        </div>
      </div>

      {/* List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {visible.length === 0 && (
          <div className="md:col-span-2 lg:col-span-3 surface-card rounded-2xl py-16 text-center text-[12px] font-mono uppercase tracking-wider text-[#1a1a1a]/60" data-testid="vv-empty">
            {busy ? "Loading…" : "No V&V items yet"}
          </div>
        )}
        {visible.map((v) => {
          const Icon = STATUS_ICON[v.status] || CircleDashed;
          const owner = uMap[v.owner_id];
          const hub = hMap[v.hub_id];
          const verifSubs = (v.sub_tasks || []).filter((s) => s.section === "verification");
          const valSubs = (v.sub_tasks || []).filter((s) => s.section === "validation");
          return (
            <Link key={v.id} to={`/app/vv/${v.id}`} data-testid={`vv-card-${v.id}`}
              className="surface-card rounded-2xl p-5 hover:shadow-lift transition-all group">
              <div className="flex items-start justify-between gap-2 mb-3">
                <span className="font-mono text-[10px] font-bold text-[#ff4500] uppercase tracking-wider">{v.display_id}</span>
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                  style={{ background: `${STATUS_COLOR[v.status] || "#1a1a1a"}18`, color: STATUS_COLOR[v.status] || "#1a1a1a" }}>
                  <Icon className="w-3 h-3" />
                  {(v.status || "open").replace("_", " ")}
                </span>
              </div>
              <div className="font-display font-black text-base leading-snug text-[#1a1a1a] group-hover:text-[#ff4500] mb-2 line-clamp-2">
                {v.title}
              </div>
              {v.description && (
                <p className="text-xs text-[#1a1a1a]/60 line-clamp-2 mb-3">{v.description}</p>
              )}
              <div className="flex flex-wrap gap-2 text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">
                {v.due_date && (
                  <span className="inline-flex items-center gap-1"><CalIcon className="w-3 h-3" /> due {formatDate(v.due_date)}</span>
                )}
                <span className="inline-flex items-center gap-1 text-[#1a1a1a]/80 font-bold">
                  {SCHEDULES.find((s) => s.k === v.schedule_type)?.l || v.schedule_type}
                </span>
              </div>
              <div className="mt-3 flex items-center gap-3 text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">
                <span>V: {verifSubs.length}</span>
                <span>V&apos;: {valSubs.length}</span>
                {(v.linked_action_ids || []).length > 0 && (
                  <span className="text-[#ff4500] font-bold">🔗 {v.linked_action_ids.length}</span>
                )}
              </div>
              <div className="mt-3 pt-3 border-t border-[#1a1a1a]/10 flex items-center justify-between text-[10px]">
                <span className="font-mono uppercase tracking-wider text-[#1a1a1a]/60">
                  {owner?.name || "Unassigned"}{hub?.name ? ` · ${hub.name}` : ""}
                </span>
                <ChevronRight className="w-3.5 h-3.5 text-[#1a1a1a]/40 group-hover:text-[#ff4500]" />
              </div>
            </Link>
          );
        })}
      </div>

      <NewVVItemDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={load}
        users={users}
        hubs={hubs}
      />
      <TemplateLibraryDialog
        open={libOpen}
        onClose={() => setLibOpen(false)}
        kind="vv"
        onInstalled={load}
      />
    </div>
  );
}

function NewVVItemDialog({ open, onClose, onCreated, users, hubs }) {
  const [form, setForm] = useState({
    title: "", description: "", owner_id: "", hub_id: "",
    due_date: "", schedule_type: "once", attendee_user_ids: [],
    // iter57 — recurrence
    rec_type: "none",           // none | rule | dates
    rec_cadence: "monthly",     // for rule
    rec_start: "",
    rec_horizon_months: 24,
    rec_dates: [""],            // for type=dates
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm({
      title: "", description: "", owner_id: "", hub_id: "", due_date: "", schedule_type: "once",
      attendee_user_ids: [], rec_type: "none", rec_cadence: "monthly", rec_start: "",
      rec_horizon_months: 24, rec_dates: [""],
    });
  }, [open]);

  const toggleAttendee = (uid) => {
    setForm((f) => {
      const set = new Set(f.attendee_user_ids || []);
      set.has(uid) ? set.delete(uid) : set.add(uid);
      return { ...f, attendee_user_ids: [...set] };
    });
  };

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      const body = { ...form };
      // Strip recurrence-scaffolding fields
      const { rec_type, rec_cadence, rec_start, rec_horizon_months, rec_dates, ...rest } = body;
      Object.keys(rest).forEach((k) => { if (rest[k] === "") delete rest[k]; });
      if (rec_type === "rule") {
        rest.recurrence = { type: "rule", cadence: rec_cadence, start_date: rec_start || null, horizon_months: Number(rec_horizon_months) || 24 };
      } else if (rec_type === "dates") {
        const dates = rec_dates.map((d) => (d || "").trim()).filter(Boolean);
        if (dates.length === 0) { toast.error("Add at least one date"); setBusy(false); return; }
        rest.recurrence = { type: "dates", dates };
      }
      await api.post("/vv-items", rest);
      toast.success("V&V item created" + (rec_type !== "none" ? " · occurrences scheduled" : ""));
      onCreated?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const addRecDate = () => setForm((f) => ({ ...f, rec_dates: [...f.rec_dates, ""] }));
  const setRecDate = (i, v) => setForm((f) => ({ ...f, rec_dates: f.rec_dates.map((x, ix) => ix === i ? v : x) }));
  const removeRecDate = (i) => setForm((f) => ({ ...f, rec_dates: f.rec_dates.filter((_, ix) => ix !== i) }));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display font-black tracking-tight">New V&amp;V Item</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/70">Title *</label>
            <input data-testid="vv-new-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Review Pest Control Program"
              className="mt-1 w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/70">Description / Scope</label>
            <textarea data-testid="vv-new-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              placeholder="What ISO/SQF clause(s) does this cover? Any special context?"
              className="mt-1 w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/70">Owner</label>
              <select data-testid="vv-new-owner" value={form.owner_id} onChange={(e) => setForm({ ...form, owner_id: e.target.value })}
                className="mt-1 w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">— Unassigned —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/70">Hub</label>
              <select data-testid="vv-new-hub" value={form.hub_id} onChange={(e) => setForm({ ...form, hub_id: e.target.value })}
                className="mt-1 w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">— None —</option>
                {hubs.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/70">Due Date</label>
              <input type="date" data-testid="vv-new-due" value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                className="mt-1 w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/70">Frequency</label>
              <select data-testid="vv-new-freq" value={form.schedule_type} onChange={(e) => setForm({ ...form, schedule_type: e.target.value })}
                className="mt-1 w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm bg-white">
                {SCHEDULES.map((s) => <option key={s.k} value={s.k}>{s.l}</option>)}
              </select>
            </div>
          </div>

          {/* iter57 — Recurrence config */}
          <div className="border-t-2 border-[#1a1a1a]/06 pt-3 mt-3">
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#ff4500] font-bold mb-2">// Recurrence — stacks occurrences for one master card</div>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <label className="text-[11px] flex items-center gap-1"><input type="radio" checked={form.rec_type === "none"} onChange={() => setForm({ ...form, rec_type: "none" })} /> One-off</label>
              <label className="text-[11px] flex items-center gap-1"><input type="radio" data-testid="vv-rec-rule" checked={form.rec_type === "rule"} onChange={() => setForm({ ...form, rec_type: "rule" })} /> Rule (weekly/monthly/…)</label>
              <label className="text-[11px] flex items-center gap-1"><input type="radio" data-testid="vv-rec-dates" checked={form.rec_type === "dates"} onChange={() => setForm({ ...form, rec_type: "dates" })} /> Fixed dates (e.g. Spring + Fall)</label>
            </div>
            {form.rec_type === "rule" && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/70">Cadence</label>
                  <select data-testid="vv-rec-cadence" value={form.rec_cadence} onChange={(e) => setForm({ ...form, rec_cadence: e.target.value })}
                    className="mt-1 w-full border border-[#1a1a1a]/20 rounded-lg px-2 py-1.5 text-sm bg-white">
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="semi_annual">Semi-annual</option>
                    <option value="annual">Annual</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/70">Start on</label>
                  <input type="date" value={form.rec_start} onChange={(e) => setForm({ ...form, rec_start: e.target.value })}
                    className="mt-1 w-full border border-[#1a1a1a]/20 rounded-lg px-2 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/70">Pre-schedule (months)</label>
                  <input type="number" min="1" max="60" value={form.rec_horizon_months}
                    onChange={(e) => setForm({ ...form, rec_horizon_months: e.target.value })}
                    className="mt-1 w-full border border-[#1a1a1a]/20 rounded-lg px-2 py-1.5 text-sm" />
                </div>
              </div>
            )}
            {form.rec_type === "dates" && (
              <div className="space-y-1.5">
                {form.rec_dates.map((d, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input type="date" value={d} onChange={(e) => setRecDate(i, e.target.value)}
                      data-testid={`vv-rec-date-${i}`}
                      className="border border-[#1a1a1a]/20 rounded-lg px-2 py-1.5 text-sm" />
                    {form.rec_dates.length > 1 && (
                      <button type="button" onClick={() => removeRecDate(i)} className="text-[11px] text-[#1a1a1a]/50 hover:text-[#ff4500]">remove</button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={addRecDate} data-testid="vv-rec-add-date"
                  className="btn-pill btn-pill-secondary text-[10px] mt-1">+ Add another date</button>
              </div>
            )}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/70">Attendees (optional)</label>
            <div className="mt-1 border border-[#1a1a1a]/20 rounded-lg p-2 max-h-32 overflow-auto flex flex-wrap gap-1.5">
              {users.length === 0 && <div className="text-[11px] text-[#1a1a1a]/40">No users visible</div>}
              {users.map((u) => {
                const on = form.attendee_user_ids.includes(u.id);
                return (
                  <button key={u.id} type="button" onClick={() => toggleAttendee(u.id)}
                    data-testid={`vv-attendee-${u.id}`}
                    className={`text-[11px] px-2 py-0.5 rounded-full border ${on ? "bg-[#ff4500] text-white border-[#ff4500]" : "bg-white text-[#1a1a1a] border-[#1a1a1a]/20"}`}>
                    {u.name || u.email}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter className="flex gap-2">
          <button onClick={onClose} className="btn-pill btn-pill-ghost" data-testid="vv-new-cancel">
            <X className="w-3.5 h-3.5" /> Cancel
          </button>
          <button onClick={submit} disabled={busy} data-testid="vv-new-submit"
            className="btn-pill btn-pill-primary disabled:opacity-50">
            <ClipboardCheck className="w-3.5 h-3.5" /> {busy ? "Creating…" : "Create"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
